/**
 * Stops the content jumping when the page first scrolls.
 *
 * On collection, home and search pages the theme swaps to a compact layout as soon as you scroll 10px: the big
 * category block leaves the page and the header shrinks, so everything below moves up by 90 to 180px in one frame.
 * Scroll anchoring cannot hide a shift that large this close to the top, and Safari has no scroll anchoring at all. This
 * keeps the product grid where it was by adding the same amount of space above it while the compact layout is on, and
 * removes it at the top again. It only acts when the grid has actually moved, and it never changes what is shown.
 */
(() => {
  const init = () => {
    const grid = document.querySelector('.product-grid-container') || document.querySelector('#product-grid');
    const host = grid && grid.closest('.colListView');
    if (!grid || !host) return;

    let restTop = null; // where the grid sits (page coordinates) while the page is at the top
    let added = 0; // extra space currently added above the grid
    let startY = null; // how far the page had scrolled when the compact layout switched on
    const top = () => grid.getBoundingClientRect().top + window.pageYOffset;
    const atTop = () => window.pageYOffset < 10;
    const rest = () => { if (atTop() && !added) restTop = top(); };
    const header = document.querySelector('.section-header');
    let lag = -1;
    const follow = () => {
      if (!header) return;
      const v = Math.max(0, Math.round(header.getBoundingClientRect().top));
      if (v !== lag) { lag = v; document.documentElement.style.setProperty('--cm-lag', v + 'px'); }
    };

    const update = () => {
      follow();
      if (atTop()) {
        if (added) { host.style.paddingTop = ''; added = 0; }
        startY = null;
        rest();
        return;
      }
      if (restTop === null) return;
      if (startY === null) startY = window.pageYOffset;
      // how far the compact layout pulled the grid up, measured without our own padding
      const shrink = restTop - (top() - added);
      // Hold all of it at the moment of the switch, then give it back as you scroll further (and take it back when you
      // scroll up again), so the grid never jumps and no permanent gap is left: the gap closes over the next few
      // hundred pixels of scrolling.
      const want = shrink > 2 ? Math.max(0, Math.min(shrink, shrink - (window.pageYOffset - startY))) : 0;
      if (Math.abs(want - added) > 0.5) {
        const base = parseFloat(getComputedStyle(host).paddingTop) - added;
        host.style.paddingTop = base + want + 'px';
        added = want;
      }
    };

    rest();
    window.addEventListener('load', rest);
    window.addEventListener('resize', () => { if (added) { host.style.paddingTop = ''; added = 0; } startY = null; rest(); });
    // Measure in the next animation frame: the theme's own scroll handlers (which switch the compact layout on) have run
    // by then, and the frame has not been painted yet, so the fix lands before anything is drawn. The header also
    // shrinks with a short animation, so after the layout switches keep measuring every frame for a moment.
    let queued = false;
    let wasTop = atTop();
    let burstUntil = 0;
    const frame = () => {
      queued = false;
      update();
      if (performance.now() < burstUntil) { queued = true; requestAnimationFrame(frame); }
    };
    window.addEventListener('scroll', () => {
      const nowTop = atTop();
      if (nowTop !== wasTop) { wasTop = nowTop; burstUntil = performance.now() + 900; }
      if (!queued) { queued = true; requestAnimationFrame(frame); }
    }, { passive: true });
    setInterval(rest, 600); // images above the grid can load late; keep the resting position current while at the top
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
